import { useCallback, useEffect, useState } from "react";

// ── usePwaInstall ────────────────────────────────────────────────────────────
// Captures the browser's `beforeinstallprompt` event so the app can offer its own
// "Install app" button, and reports install state.
//
// Returns:
//   canInstall   – true when the browser has offered an install prompt we can fire
//                  (Chrome/Edge/Android/desktop Chromium). Not fired on iOS Safari.
//   isInstalled  – true when running as an installed app (standalone display mode)
//                  or after the user completes installation this session.
//   isIOS        – true on iOS/iPadOS Safari, where install is manual (Share → Add
//                  to Home Screen); use this to show a hint instead of a button.
//   promptInstall – async fn that shows the native install dialog. Resolves to
//                  "accepted" | "dismissed" | "unavailable".
export default function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  const isStandalone = useCallback(
    () =>
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      // iOS Safari exposes standalone on navigator instead of matchMedia.
      window.navigator.standalone === true,
    []
  );

  const isIOS =
    typeof window !== "undefined" &&
    /iphone|ipad|ipod/i.test(window.navigator.userAgent || "") &&
    // Exclude in-app browsers that aren't Safari-based where this hint won't help.
    !/crios|fxios|edgios/i.test(window.navigator.userAgent || "");

  useEffect(() => {
    if (isStandalone()) setIsInstalled(true);

    const onBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar so we control when/where the prompt shows.
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, [isStandalone]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return "unavailable";
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    // The prompt can only be used once; drop it either way.
    setDeferredPrompt(null);
    return outcome; // "accepted" | "dismissed"
  }, [deferredPrompt]);

  // Dev-only: in `npm start` the service worker isn't registered, so
  // `beforeinstallprompt` never fires and the button would never show. This flag
  // lets the button render during development so it can be seen/styled. It is
  // always false in production, where real install detection applies.
  const devPreview = process.env.NODE_ENV !== "production" && !isInstalled;

  return {
    canInstall: Boolean(deferredPrompt) && !isInstalled,
    isInstalled,
    isIOS: isIOS && !isInstalled,
    devPreview,
    promptInstall,
  };
}
